import { useEffect, useRef } from 'react';
import { App } from '@capacitor/app';
import { Capacitor } from '@capacitor/core';

import {
	useBusinessLayer,
	useLiveUpdateVersion,
	UserActionTypes,
	useUser,
} from '@mopla/business-logic';

import {
	pushAppInfo,
	pushUserId,
} from './googleTagManager/googleTagManagerUtils';

/**
 * Hook that tracks user and app version data to Google Analytics.
 * Automatically pushes userId and app info to the GTM dataLayer when they become available.
 */
export function useAnalyticsTracking() {
	const user = useUser();
	const { watchActions$ } = useBusinessLayer();
	const { liveUpdateVersion } = useLiveUpdateVersion();
	const appInfoPushed = useRef(false);

	useEffect(() => {
		const userId = user?.userData?.userId;

		if (!userId) return;

		pushUserId(userId);
	}, [user?.userData?.userId]);

	// Reset the user id on sign-out / account deletion, otherwise we keep
	// attributing events to the previous user. Listens to SignOut (not
	// SignOutResult), because LogoutWrapper unmounts the app when it starts.
	useEffect(() => {
		const sub = watchActions$([
			UserActionTypes.SignOut,
			UserActionTypes.DeleteCurrentUserResult,
		]).subscribe((action) => {
			if (
				action.type === UserActionTypes.DeleteCurrentUserResult &&
				action.payload?.error
			) {
				return;
			}
			pushUserId(null);
		});

		return () => sub.unsubscribe();
	}, [watchActions$]);

	useEffect(() => {
		if (appInfoPushed.current) return;

		const isNative = Capacitor.isNativePlatform();

		if (isNative && !liveUpdateVersion) return;

		const pushInfo = async () => {
			const platform = Capacitor.getPlatform() as 'web' | 'ios' | 'android';

			let nativeVersion: string | undefined;
			let nativeBuild: string | undefined;

			if (isNative) {
				try {
					const appInfo = await App.getInfo();
					nativeVersion = appInfo.version;
					nativeBuild = appInfo.build;
				} catch (e) {
					console.error('Failed to get app info', e);
				}
			}

			pushAppInfo({
				platform,
				is_native: isNative,
				native_version: nativeVersion,
				native_build: nativeBuild,
				live_update_version: liveUpdateVersion?.buildId,
			});

			appInfoPushed.current = true;
		};

		pushInfo();
	}, [liveUpdateVersion]);
}
